import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  CheckCircle,
  Info,
  Warning,
  WarningCircle,
  X,
} from '@phosphor-icons/react';
import { cn } from '../../utils/cn';

/* ================================================================= */
/* Types                                                              */
/* ================================================================= */

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastData {
  id: string;
  variant: ToastVariant;
  title: string;
  message?: string;
  /** Auto-dismiss duration in ms.  0 = persist until manual dismiss. Default 5000. */
  duration?: number;
}

/* ================================================================= */
/* Variant config                                                     */
/* ================================================================= */

const variantConfig: Record<
  ToastVariant,
  { border: string; text: string; icon: typeof Info }
> = {
  success: {
    border: 'border-l-success',
    text: 'text-success-text',
    icon: CheckCircle,
  },
  error: {
    border: 'border-l-error',
    text: 'text-error-text',
    icon: WarningCircle,
  },
  info: {
    border: 'border-l-info',
    text: 'text-info-text',
    icon: Info,
  },
  warning: {
    border: 'border-l-warning',
    text: 'text-warning-text',
    icon: Warning,
  },
};

/* ================================================================= */
/* ToastItem                                                          */
/* ================================================================= */

interface ToastItemProps {
  toast: ToastData;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const [exiting, setExiting] = useState(false);
  const config = variantConfig[toast.variant];
  const Icon = config.icon;
  const duration = toast.duration ?? 5000;

  // Auto-dismiss timer
  useEffect(() => {
    if (duration <= 0) return;
    const timer = setTimeout(() => setExiting(true), duration);
    return () => clearTimeout(timer);
  }, [duration]);

  // After exit animation, actually remove
  useEffect(() => {
    if (!exiting) return;
    const timer = setTimeout(() => onDismiss(toast.id), 200);
    return () => clearTimeout(timer);
  }, [exiting, toast.id, onDismiss]);

  const handleDismiss = () => setExiting(true);

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        'flex items-start gap-3 w-full max-w-sm',
        'bg-bg-elevated border-l-[3px] rounded-lg shadow-lg p-4',
        config.border,
        exiting ? 'animate-slide-out-right' : 'animate-slide-in-right',
      )}
    >
      <Icon
        size={20}
        weight="fill"
        className={cn('shrink-0 mt-0.5', config.text)}
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-text-primary">{toast.title}</p>
        {toast.message && (
          <p className="text-xs text-text-secondary mt-0.5">{toast.message}</p>
        )}
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="shrink-0 text-text-tertiary hover:text-text-primary transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus rounded min-h-[44px] min-w-[44px] flex items-center justify-center lg:min-h-0 lg:min-w-0"
        aria-label="Fechar notificacao"
      >
        <X size={14} />
      </button>
    </div>
  );
}

/* ================================================================= */
/* ToastContainer                                                     */
/* ================================================================= */

interface ToastContainerProps {
  toasts: ToastData[];
  onDismiss: (id: string) => void;
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return createPortal(
    <div
      aria-label="Notificacoes"
      className="fixed top-4 right-4 z-[var(--z-toast)] flex flex-col gap-2 pointer-events-none"
    >
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem toast={toast} onDismiss={onDismiss} />
        </div>
      ))}
    </div>,
    document.body,
  );
}

/* ================================================================= */
/* Context + Provider + Hook                                          */
/* ================================================================= */

type AddToastInput = Omit<ToastData, 'id'>;

interface ToastContextValue {
  /** Add a toast notification. Returns the auto-generated toast id. */
  addToast: (toast: AddToastInput) => string;
  /** Remove a toast by id */
  removeToast: (id: string) => void;
  /** Current toasts (read-only) */
  toasts: readonly ToastData[];
}

const ToastContext = createContext<ToastContextValue | null>(null);

let toastCounter = 0;

/**
 * Provides toast state and the `<ToastContainer>` portal.
 * Wrap your app (or layout) with this provider once.
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const addToast = useCallback((input: AddToastInput): string => {
    const id = `toast-${++toastCounter}-${Date.now()}`;
    const toast: ToastData = { ...input, id };
    setToasts((prev) => [...prev, toast]);
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value = useMemo(
    () => ({ addToast, removeToast, toasts }),
    [addToast, removeToast, toasts],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
}

/**
 * Hook to add/remove toast notifications from any component.
 *
 * Must be used within a `<ToastProvider>`.
 *
 * @example
 * ```tsx
 * const { addToast } = useToast();
 * addToast({ variant: 'success', title: 'Saved!' });
 * ```
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a <ToastProvider>');
  }
  return ctx;
}

export default ToastContainer;
